import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { colors, typography } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from './Switch';
import { supabase } from '../config/supabase';
import { Recipe } from '../types/recipe';
import { createMuxUpload } from '../utils/mux';
import { useToast } from '../contexts/ToastContext';
import { X } from '@phosphor-icons/react';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${colors.neutral.charcoal};
`;

const SwitchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const VideoUploadArea = styled.div`
  border: 2px dashed ${colors.neutral.lightCharcoal};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.brand.laurel};
  }
`;

const VideoPreview = styled.video`
  width: 100%;
  max-height: 300px;
  border-radius: 4px;
  margin-top: 1rem;
`;

const RecipeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${colors.brand.laurel};
  color: ${colors.neutral.white};
  padding: 12px;
  border-radius: 8px;
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  line-height: ${typography.label.large.lineHeight};
`;

const RemoveChipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${colors.neutral.white};
  cursor: pointer;
  padding: 0;
  margin: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const RecipeChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const RecipeList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${colors.neutral.white};
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  display: none;

  &.visible {
    display: block;
  }
`;

const RecipeListItem = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  background-color: ${colors.neutral.white};
  color: ${colors.neutral.black};
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
  }
`;

interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
}

export function VideoUploadModal({ isOpen, onClose, onUploadComplete }: VideoUploadModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [isFree, setIsFree] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showRecipeList, setShowRecipeList] = useState(false);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
  const recipesRef = useRef<HTMLDivElement>(null);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedRecipes([]);
    setIsFree(true);
    setIsLive(false);
    setSelectedVideo(null);
    setVideoPreview(null);
    setRecipeSearchTerm('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const fetchRecipes = async () => {
      if (!user?.id) return;

      try {
        // Get the creator's ID from CreatorDetails using the auth user's ID
        const { data: creatorDetails, error: creatorError } = await supabase
          .from('CreatorDetails')
          .select('id')
          .eq('AuthID', user.id)
          .single();

        if (creatorError) {
          console.error('Error fetching creator details:', creatorError);
          return;
        }

        if (!creatorDetails) {
          console.error('No creator details found for user');
          return;
        }

        const creatorId = creatorDetails.id;

        const { data, error } = await supabase
          .from('Recipes')
          .select('*')
          .eq('UserID', creatorId)
          .order('DateCreated', { ascending: false });

        if (error) throw error;

        setRecipes(data || []);
        setFilteredRecipes(data || []);
      } catch (error) {
        console.error('Error fetching recipes:', error);
      }
    };

    if (isOpen) {
      fetchRecipes();
    }
  }, [user?.id, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (recipesRef.current && !recipesRef.current.contains(event.target as Node)) {
        setShowRecipeList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      showToast('Video size must be less than 100MB', 'error');
      return;
    }

    setSelectedVideo(file);
    const url = URL.createObjectURL(file);
    setVideoPreview(url);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedVideo || !user?.id) return;

    setIsUploading(true);
    
    try {
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        throw new Error('Could not verify creator account.');
      }

      if (!creatorDetails) {
        throw new Error('No creator account found for the current user.');
      }

      const creatorId = creatorDetails.id;

      // Upload video to Mux
      const muxResponse = await createMuxUpload(selectedVideo, title, user.id);

      // Save video details to database
      const { data: videoData, error: videoError } = await supabase
        .from('Videos')
        .insert([
          {
            MuxAssetId: muxResponse.id,
            MuxPlaybackId: muxResponse.playback_id,
            Live: isLive,
            CreatorID: creatorId,
            Free: isFree,
            Title: title,
            Description: description,
          },
        ])
        .select()
        .single();

      if (videoError) throw videoError;

      // Save video-recipe relationships
      if (selectedRecipes.length > 0) {
        const videoRecipePayload = selectedRecipes.map(recipeId => ({
          VideoId: videoData.VideoId,
          RecipeId: parseInt(recipeId),
        }));
        const { error: recipeError } = await supabase
          .from('VideoRecipes')
          .insert(videoRecipePayload);
        if (recipeError) throw recipeError;
      }

      showToast('Video uploaded successfully', 'success');
      resetForm();
      onClose();
      onUploadComplete?.();
    } catch (error) {
      showToast('Failed to upload video', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRecipeSearch = (value: string) => {
    setRecipeSearchTerm(value);
    setShowRecipeList(true);
    if (!value.trim()) {
      setFilteredRecipes(recipes);
    } else {
      setFilteredRecipes(
        recipes.filter(recipe =>
          recipe.Name.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  const handleRecipeSelect = (recipe: Recipe) => {
    if (!selectedRecipes.includes(recipe.RecipeId.toString())) {
      setSelectedRecipes(prev => [...prev, recipe.RecipeId.toString()]);
    }
    setRecipeSearchTerm('');
  };

  const handleRecipeRemove = (recipeId: string) => {
    setSelectedRecipes(prev => prev.filter(id => id !== recipeId));
  };

  const getSelectedRecipeNames = () => {
    return selectedRecipes.map(recipeId => {
      const recipe = recipes.find(r => r.RecipeId.toString() === recipeId);
      return recipe?.Name || '';
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload Video">
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Video</Label>
          <VideoUploadArea onClick={() => fileInputRef.current?.click()}>
            {videoPreview ? (
              <VideoPreview src={videoPreview} controls />
            ) : (
              <p>Click to select a video file (max 100MB)</p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoSelect}
              style={{ display: 'none' }}
            />
          </VideoUploadArea>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
            placeholder="Enter video title"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="description">Description</Label>
          <TextArea
            id="description"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            placeholder="Enter video description"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="recipes">Related Recipes (up to 10)</Label>
          <div style={{ position: 'relative' }} ref={recipesRef}>
            <Input
              type="text"
              id="recipes"
              value={recipeSearchTerm}
              onChange={(e) => handleRecipeSearch(e.target.value)}
              onFocus={() => setShowRecipeList(true)}
              placeholder="Search recipes"
            />
            <RecipeList className={showRecipeList ? 'visible' : ''}>
              {filteredRecipes
                .filter(recipe => !selectedRecipes.includes(recipe.RecipeId.toString()))
                .map((recipe) => (
                  <RecipeListItem
                    key={recipe.RecipeId}
                    onClick={() => handleRecipeSelect(recipe)}
                  >
                    {recipe.Name}
                  </RecipeListItem>
                ))}
            </RecipeList>
          </div>
          {selectedRecipes.length > 0 && (
            <RecipeChipsContainer>
              {getSelectedRecipeNames().map((name, index) => (
                <RecipeChip key={selectedRecipes[index]}>
                  {name}
                  <RemoveChipButton onClick={() => handleRecipeRemove(selectedRecipes[index])}>
                    <X size={20} />
                  </RemoveChipButton>
                </RecipeChip>
              ))}
            </RecipeChipsContainer>
          )}
        </FormGroup>

        <SwitchGroup>
          <Label>Free Video</Label>
          <Switch checked={isFree} onChange={setIsFree} />
        </SwitchGroup>

        <SwitchGroup>
          <Label>Make Live</Label>
          <Switch checked={isLive} onChange={setIsLive} />
        </SwitchGroup>

        <Button type="submit" disabled={isUploading || !selectedVideo}>
          {isUploading ? 'Uploading...' : 'Upload Video'}
        </Button>
      </Form>
    </Modal>
  );
} 